import axios from 'axios';
import type {
  RelationshipGraph,
  RelationshipStatistics,
  SuspiciousPattern,
  RelationshipFilters,
  ExportResponse,
  Relationship
} from '../types/relationship';
import { API_URL } from '../config/apiConfig';

const RELATIONSHIP_SERVICE_URL = `${API_URL}/api/v1/relationships`;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Create axios instance with auth
const api = axios.create({
  baseURL: RELATIONSHIP_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const relationshipService = {
  /**
   * Get all relationships for a guest
   */
  async getGuestRelationships(
    guestId: string,
    minStrength: string = 'WEAK',
    includeWeak: boolean = true
  ): Promise<Relationship[]> {
    const response = await api.get(`/guest/${guestId}`, {
      params: { min_strength: minStrength, include_weak: includeWeak },
    });
    return response.data.relationships;
  },

  /**
   * Get relationship graph for a guest
   */
  async getRelationshipGraph(
    guestId: string,
    filters: RelationshipFilters
  ): Promise<RelationshipGraph> {
    const response = await api.get(`/graph/${guestId}`, {
      params: {
        max_depth: filters.maxDepth,
        include_weak: filters.includeWeak,
      },
    });
    return response.data;
  },

  /**
   * Get relationship statistics for a guest
   */
  async getStatistics(guestId: string): Promise<RelationshipStatistics> {
    const response = await api.get(`/statistics/${guestId}`);
    return response.data;
  },

  /**
   * Get suspicious patterns
   */
  async getSuspiciousPatterns(
    status: string = 'NEW',
    severity?: string
  ): Promise<SuspiciousPattern[]> {
    const response = await api.get('/suspicious', {
      params: { status, severity },
    });
    return response.data.patterns;
  },

  /**
   * Analyze relationships with custom parameters
   */
  async analyzeRelationships(
    guestId: string,
    filters: RelationshipFilters
  ): Promise<RelationshipGraph> {
    const response = await api.post('/analyze', {
      guest_id: guestId,
      max_depth: filters.maxDepth,
      min_strength: filters.minStrength,
      include_weak: filters.includeWeak,
    });
    return response.data;
  },

  /**
   * Export relationship graph
   */
  async exportGraph(
    guestId: string,
    format: 'JSON' | 'CSV' | 'PNG',
    filters: RelationshipFilters
  ): Promise<ExportResponse> {
    const response = await api.post('/export', {
      guest_id: guestId,
      format,
      max_depth: filters.maxDepth,
      include_weak: filters.includeWeak,
    });
    return response.data;
  },
};
